import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Bot, X, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import config from '../config';
import Markdown from 'react-markdown'

// Configuration for API endpoints
const API_BASE_URL = `${config.API_BASE_URL}`; // IMPORTANT: Replace with your actual API URL
const API_EXTRACT_URL = `${API_BASE_URL}/extract-warranty-claim`;
const API_PREDICT_URL = `${API_BASE_URL}/predict-from-json-dmy/`;

// Helper component for styled form cards
const FormCard = ({ title, icon, children, headerContent }) => (
    <div className="bg-surface p-8 border border-mazda-border mb-8 rounded-none">
        <div className="flex justify-between items-center border-b border-mazda-border pb-4 mb-6">
            <div className="flex items-center">
                <span className="text-text-main mr-4 text-lg"><i className={`fas ${icon}`}></i></span>
                <h5 className="text-sm font-semibold text-text-main uppercase tracking-widest">{title}</h5>
            </div>
            {headerContent}
        </div>
        {children}
    </div>
);

// Helper component for the main loader/spinner
const Loader = ({ text }) => (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex flex-col justify-center items-center z-50 backdrop-blur-sm">
        <div className="relative flex justify-center items-center mb-6">
            <div className="absolute w-16 h-16 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <Bot className="text-white h-6 w-6" />
        </div>
        <p className="text-white text-sm uppercase tracking-widest font-semibold">{text}</p>
    </div>
);

// Helper component for Validation Error Modal
const ValidationModal = ({ isOpen, onClose, errors }) => {
    if (!isOpen) return null;

    // Map internal error keys to human-readable labels
    const formatErrorKey = (key) => {
        if (key.startsWith('part_')) return `Part ${parseInt(key.split('_')[1]) + 1} Details`;
        if (key.startsWith('labor_')) return `Labor Op ${parseInt(key.split('_')[1]) + 1} Details`;
        const map = {
            VIN: 'VIN',
            ModelName: 'Model Name',
            CustomerName: 'Customer Name',
            EstimatedAmount: 'Estimated Total Amount',
            PartsUsed: 'Parts Used (At least one required)',
            LaborOpDetails: 'Labor Operations (At least one required)'
        };
        return map[key] || key;
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex justify-center items-center z-50" onClick={onClose}>
            <div className="bg-surface rounded-none border-t-4 border-red-600 border-x border-b border-mazda-border w-full max-w-lg mx-4 flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
                <header className="flex justify-between items-center p-6 border-b border-mazda-border bg-background">
                    <div className="flex items-center space-x-4">
                        <AlertTriangle className="h-6 w-6 text-red-600" />
                        <h5 className="text-sm font-semibold text-red-600 uppercase tracking-widest">Required Fields Missing</h5>
                    </div>
                    <button onClick={onClose} className="p-2 text-text-muted hover:text-text-main transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                </header>
                <div className="p-8 max-h-[60vh] overflow-y-auto">
                    <p className="text-xs font-semibold text-text-main mb-6 uppercase tracking-wider">Please provide information for the following fields to proceed:</p>
                    <ul className="space-y-3">
                        {Object.entries(errors).map(([key, msg]) => (
                            <li key={key} className="flex items-start space-x-3 text-sm text-text-main bg-red-50 p-3 border-l-2 border-red-600">
                                <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                                <span className="font-semibold tracking-wide uppercase text-xs mt-0.5">{formatErrorKey(key)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <footer className="p-6 border-t border-mazda-border bg-background flex justify-end">
                    <button onClick={onClose} className="bg-red-600 hover:bg-red-700 text-white py-3 px-8 text-xs uppercase tracking-widest font-semibold transition-colors rounded-none">
                        Review Form
                    </button>
                </footer>
            </div>
        </div>
    );
};

// Helper component for the Prediction Modal
const PredictionModal = ({ isOpen, onClose, data }) => {
    if (!isOpen || !data) return null;

    const reasonCodeMap = new Map([
        ["ACP", "Warranty claim accepted."],
        ["PID", "Incorrect, ineligible, or improperly identified Part Number Main Cause (PNMC) or related part."],
        ["QTY", "Incorrect quantity or usage of PNMC or related part."],
        ["LOP", "Invalid or incorrect labor operation (LO)."],
        ["ATH", "Claim requires pre-authorization or submission authorization."],
        ["DSA", "Dealer Self-Authorization (DSA) error."],
        ["TML", "Claim, resubmission, or appeal submitted past the allowed deadline."],
        ["DUP", "Duplicate claim or repeat repair."],
        ["CMP", "Conflict with Campaign/Recall/SSP/Process Number requirements."],
        ["WEX", "General warranty coverage for the claimed repair has expired."],
        ["DCO", "Incorrect, missing, or improperly formatted data."],
        ["SUB", "Error in sublet claim."],
        ["BAT", "Battery claim non-compliance."],
        ["NPF", "No Problem Found (NPF) procedure violation."],
        ["BRV", "Claim on a branded title vehicle where warranty is void."],
        ["APL", "Appeal process error."],
        ["DOC", "Required documentation or claim text missing or incomplete."],
        ["TSB", "Non-compliance with Technical Service Bulletin (TSB) requirements."],
        ["PGM", "Vehicle programming/software issue."],
        ["FIN", "Financial error in claim total or amounts."],
        ["RNT", "Rental claim error."],
        ["MSC", "Miscellaneous policy violation or system error."]
    ]);

    const statusMap = { 'Accepted': 'Accepted', 'Rejected': 'Rejected' };
    const status = statusMap[data.warranty_status] || "N/A";
    const reason = reasonCodeMap.get(data.reason) || data.reason || "No reason provided.";
    const statusProb = (data.warranty_status_probability || 0) * 100;

    const isAccepted = status === 'Accepted';
    
    // Dynamic styling based on status
    const statusColorClass = isAccepted ? 'text-green-500' : 'text-red-500';
    const barColorClass = isAccepted ? 'bg-green-600' : 'bg-red-600';
    const confidenceLabel = isAccepted ? "Acceptance Confidence" : "Rejection Confidence";
    
    const headerIconColor = 'text-text-main';
    const statusDisplayClass = `bg-transparent border border-mazda-border ${statusColorClass}`;
    const reasonBorderClass = 'border-l-2 border-text-main';
    const StatusIcon = isAccepted ? CheckCircle2 : XCircle;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex justify-center items-center z-40" onClick={onClose}>
            <div className="bg-surface rounded-none border border-mazda-border w-full max-w-2xl mx-4 flex flex-col" onClick={e => e.stopPropagation()}>
                <header className="flex justify-between items-center p-6 border-b border-mazda-border">
                    <div className="flex items-center space-x-4">
                        <Bot className={`h-6 w-6 ${headerIconColor}`} />
                        <h5 className="text-sm font-semibold text-text-main uppercase tracking-widest">AI Claim Prediction</h5>
                    </div>
                    <button onClick={onClose} className="p-2 text-text-muted hover:text-text-main transition-colors">
                        <X className="h-5 w-5" />
                    </button>
                </header>

                <div className="p-8 space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                        <div>
                            <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">Predicted Status</label>
                            <div className={`flex items-center justify-center space-x-3 p-4 font-semibold text-sm uppercase tracking-widest ${statusDisplayClass}`}>
                                <StatusIcon className="h-5 w-5" />
                                <span>{status}</span>
                            </div>
                        </div>
                        <div>
                            {/* Updated Confidence Section */}
                            <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">
                                {confidenceLabel}
                            </label>
                            <div className="w-full bg-background border border-mazda-border h-12 overflow-hidden relative">
                                <div
                                    className={`h-full flex items-center ${barColorClass} justify-center text-white font-semibold text-xs tracking-widest transition-all duration-500`}
                                    style={{ width: `${statusProb.toFixed(1)}%` }}
                                >
                                    {statusProb.toFixed(1)}%
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-3">
                            Predicted Reason
                        </label>
                        <div className={`
                                bg-background p-6 ${reasonBorderClass} 
                                max-h-[300px] overflow-y-auto 
                                [&::-webkit-scrollbar]:w-2 
                                [&::-webkit-scrollbar-track]:bg-transparent 
                                [&::-webkit-scrollbar-thumb]:bg-mazda-border 
                                hover:[&::-webkit-scrollbar-thumb]:bg-text-muted 
                                [&::-webkit-scrollbar-thumb]:rounded-full
                            `}>
                            <div className="text-sm text-text-main leading-relaxed">
                                <Markdown>
                                    {reason}
                                </Markdown>
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="p-6 border-t border-mazda-border bg-transparent flex justify-end">
                    <button
                        onClick={onClose}
                        className="btn btn-primary"
                    >
                        Close
                    </button>
                </footer>
            </div>
        </div>
    );
};

const WarrentySubmitPage = () => {
    const initialFormData = {
        ClaimNumber: '', RO_number: '', VIN: '', ModelName: '', PurchasingYear: '', MiledgeIn: '', MiledgeOut: '',
        RO_open_date: '', RepairDate: '', CustomerName: '', RO_Description: '',
        PNMC: '', PNMC_Quantity: '', WarrantyType_Code: '', SymptomCode: '', DamageCode: '',
        RelatedParts: '', RepairLocation: '',
        PartsUsed: [],
        LaborOpDetails: [],
        SubletAmount: '', SubletCode: '', EstimatedAmount: '', ServiceAdvisor: '',
        DealerCode: '', DealerName: '', DealerAddress: ''
    };

    const [formData, setFormData] = useState(initialFormData);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileName, setFileName] = useState('Choose file...');
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [loaderText, setLoaderText] = useState('');
    const [feedback, setFeedback] = useState({ message: '', type: '' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
    const [predictionData, setPredictionData] = useState(null);

    const validateForm = useCallback(() => {
        const newErrors = {};

        // Helper function that properly checks for empty strings/nulls but ALLOWS 0 as a valid number
        const isEmpty = (val) => {
            if (val === undefined || val === null) return true;
            if (typeof val === 'string') return val.trim() === '';
            return false; // Valid numbers (including 0) will return false (i.e., not empty)
        };

        if (isEmpty(formData.VIN)) newErrors.VIN = "Please provide a VIN.";
        if (isEmpty(formData.ModelName)) newErrors.ModelName = "Please provide the model name.";
        if (isEmpty(formData.CustomerName)) newErrors.CustomerName = "Please provide the customer's name.";
        if (isEmpty(formData.EstimatedAmount)) newErrors.EstimatedAmount = "Please provide the estimated total amount.";

        if (!formData.PartsUsed || formData.PartsUsed.length === 0) {
            newErrors.PartsUsed = "At least one part is required.";
        } else {
            formData.PartsUsed.forEach((part, index) => {
                if (isEmpty(part.part_name) || isEmpty(part.part_quantity) || isEmpty(part.part_price)) {
                    newErrors[`part_${index}`] = "All part fields are required.";
                }
            });
        }
        
        if (!formData.LaborOpDetails || formData.LaborOpDetails.length === 0) {
            newErrors.LaborOpDetails = "At least one labor operation is required.";
        } else {
            formData.LaborOpDetails.forEach((op, index) => {
                if (isEmpty(op.LaborOpCode) || isEmpty(op.LaborHours) || isEmpty(op.TechnicianID)) {
                    newErrors[`labor_${index}`] = "All labor fields are required.";
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setFileName(file.name);
        }
    };

    const handleDynamicChange = (index, event, type) => {
        const { name, value } = event.target;
        const list = [...formData[type]];
        list[index][name] = value;
        setFormData(prev => ({ ...prev, [type]: list }));

        const errorKey = type === 'PartsUsed' ? `part_${index}` : `labor_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => ({ ...prev, [errorKey]: undefined }));
        }
    };

    const addRow = (type) => {
        const emptyRow = type === 'PartsUsed'
            ? { part_name: '', part_quantity: '', part_price: '' }
            : { LaborOpCode: '', LaborHours: '', TechnicianID: '' };
        setFormData(prev => ({ ...prev, [type]: [...prev[type], emptyRow] }));
        if (errors[type]) {
            setErrors(prev => ({ ...prev, [type]: undefined }));
        }
    };

    const removeRow = (index, type) => {
        const list = [...formData[type]];
        list.splice(index, 1);
        setFormData(prev => ({ ...prev, [type]: list }));
    };

    const showFeedback = (message, type = 'error') => {
        setFeedback({ message, type });
        if (type === 'success') {
            setTimeout(() => setFeedback({ message: '', type: '' }), 5000);
        }
    };

    const handleReset = () => {
        setFormData(initialFormData);
        setSelectedFile(null);
        setFileName('Choose file...');
        setErrors({});
        setFeedback({ message: '', type: '' });
    };

    const handleExtract = async () => {
        if (!selectedFile) {
            showFeedback('Please select an image file to upload.');
            return;
        }
        setIsLoading(true);
        setLoaderText('Analyzing Document...');
        const uploadFormData = new FormData();
        uploadFormData.append('file', selectedFile);

        try {
            const response = await axios.post(API_EXTRACT_URL, uploadFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const extractedData = response.data;
            
            // Map array data defensively in case the AI returns mismatched key casing (e.g. PartName vs part_name)
            const safePartsUsed = Array.isArray(extractedData.PartsUsed) ? extractedData.PartsUsed.map(p => ({
                part_name: p.part_name !== undefined ? p.part_name : (p.PartName || p.name || ''),
                part_quantity: p.part_quantity !== undefined ? p.part_quantity : (p.Quantity || p.quantity || ''),
                part_price: p.part_price !== undefined ? p.part_price : (p.Price || p.price || '')
            })) : [];

            const safeLaborOpDetails = Array.isArray(extractedData.LaborOpDetails) ? extractedData.LaborOpDetails.map(l => ({
                LaborOpCode: l.LaborOpCode !== undefined ? l.LaborOpCode : (l.Code || l.code || ''),
                LaborHours: l.LaborHours !== undefined ? l.LaborHours : (l.Hours || l.hours || ''),
                TechnicianID: l.TechnicianID !== undefined ? l.TechnicianID : (l.TechID || l.tech_id || '')
            })) : [];

            const formattedData = {
                ...initialFormData,
                ...extractedData,
                RelatedParts: Array.isArray(extractedData.RelatedParts) ? extractedData.RelatedParts.join(', ') : (extractedData.RelatedParts || ''),
                RepairLocation: Array.isArray(extractedData.RepairLocation) ? extractedData.RepairLocation.join(', ') : (extractedData.RepairLocation || ''),
                PartsUsed: safePartsUsed,
                LaborOpDetails: safeLaborOpDetails,
            };
            setFormData(formattedData);
            setErrors({}); // Clear any previous errors on AI fill
            setIsValidationModalOpen(false); // Make sure the error modal doesn't linger
            showFeedback('AI successfully extracted data. Please review and submit.', 'success');
        } catch (error) {
            const errorMsg = error.response?.data?.detail || "Extraction failed. Please try again.";
            showFeedback(`Error: ${errorMsg}`);
        } finally {
            setIsLoading(false);
            setSelectedFile(null);
            setFileName('Choose file...');
        }
    };

    const formatDataForApi = () => {
        const data = { ...formData };

        ['MiledgeIn', 'MiledgeOut', 'PNMC_Quantity', 'PurchasingYear', 'EstimatedAmount', 'SubletAmount'].forEach(key => {
            data[key] = data[key] ? parseFloat(data[key]) : null;
        });

        data.RelatedParts = data.RelatedParts ? String(data.RelatedParts).split(',').map(s => s.trim()).filter(Boolean) : [];
        data.RepairLocation = data.RepairLocation ? String(data.RepairLocation).split(',').map(s => s.trim()).filter(Boolean) : [];

        data.PartsUsed = data.PartsUsed.map(p => ({
            ...p,
            part_quantity: parseInt(p.part_quantity, 10) || 0,
            part_price: parseFloat(p.part_price) || 0
        }));
        data.LaborOpDetails = data.LaborOpDetails.map(l => ({
            ...l,
            LaborHours: parseFloat(l.LaborHours) || 0
        }));

        return data;
    }

    const handlePredict = async () => {
        if (!validateForm()) {
            setIsValidationModalOpen(true);
            return;
        }
        setIsLoading(true);
        setLoaderText('Predicting Status...');
        const apiData = formatDataForApi();

        try {
            const response = await axios.post(API_PREDICT_URL, apiData, {
                headers: { 'Content-Type': 'application/json' }
            });
            setPredictionData(response.data);
            setIsModalOpen(true);
        } catch (error) {
            const errorMsg = error.response?.data?.detail || "Prediction failed. Please try again.";
            showFeedback(`Prediction Error: ${errorMsg}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validateForm()) {
            setIsValidationModalOpen(true);
            return;
        }
        setIsLoading(true);
        setLoaderText('Submitting Claim...');
        setTimeout(() => {
            setIsLoading(false);
            showFeedback('Claim submitted successfully (simulated)!', 'success');
            handleReset();
        }, 1500);
    };

    const getInputClass = (fieldName) => {
        const baseClasses = "form-input transition-colors w-full";
        if (errors[fieldName]) {
            return `${baseClasses} border-red-600 focus:border-red-600 focus:ring-1 focus:ring-red-600 bg-red-50/50`;
        }
        return baseClasses;
    };

    return (
        <div className="bg-background min-h-screen font-sans">
            {isLoading && <Loader text={loaderText} />}
            <PredictionModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} data={predictionData} />
            <ValidationModal isOpen={isValidationModalOpen} onClose={() => setIsValidationModalOpen(false)} errors={errors} />

            <main className="container mx-auto p-4 md:p-8">
                <form onSubmit={handleSubmit} onReset={handleReset} noValidate>
                    {feedback.message && (
                        <div className={`p-4 mb-8 border ${feedback.type === 'success' ? 'bg-background border-text-main text-text-main' : 'bg-background border-text-main text-text-main'} text-sm uppercase tracking-widest font-semibold`} role="alert">
                            {feedback.message}
                        </div>
                    )}

                    <FormCard title="AI Data Extraction" icon="fa-magic">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                            <div>
                                <label htmlFor="claimImageUpload" className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Upload Claim Document/Image</label>
                                <div className="mt-1 flex items-center rounded-none border-2 border-dashed border-mazda-border px-6 pt-8 pb-8 bg-background">
                                    <div className="w-full text-center">
                                        <svg className="mx-auto h-10 w-10 text-text-muted mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                        <div className="flex text-sm text-text-main justify-center uppercase tracking-widest font-semibold">
                                            <label htmlFor="claimImageUpload" className="relative cursor-pointer bg-transparent font-semibold text-text-main hover:text-text-muted focus-within:outline-none">
                                                <span className="border-b border-text-main pb-1">Upload a file</span>
                                                <input id="claimImageUpload" name="claimImageUpload" type="file" className="sr-only" onChange={handleFileChange} accept="image/*" />
                                            </label>
                                        </div>
                                        <p className="mt-4 text-xs text-text-muted tracking-wider">{fileName}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="text-center lg:text-left">
                                <button type="button" onClick={handleExtract} className="btn btn-primary w-full lg:w-auto">
                                    <i className="fas fa-sync-alt mr-3"></i>
                                    Upload & Fill Form with <span className="font-bold ml-1">AI</span>
                                </button>
                            </div>
                        </div>
                    </FormCard>

                    <FormCard title="Claim & Vehicle Information" icon="fa-car-side">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Claim Number</label><input type="text" name="ClaimNumber" value={formData.ClaimNumber} onChange={handleInputChange} className={getInputClass('ClaimNumber')} placeholder="Enter Claim Number" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Repair Order Number</label><input type="text" name="RO_number" value={formData.RO_number} onChange={handleInputChange} className={getInputClass('RO_number')} placeholder="Enter R/O Number" /></div>
                            <div>
                                <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                    VIN<span className="text-red-600 ml-1 text-sm">*</span>
                                </label>
                                <input type="text" name="VIN" value={formData.VIN} onChange={handleInputChange} className={getInputClass('VIN')} placeholder="Enter VIN" required />
                            </div>
                            <div className="grid grid-cols-2 gap-x-8">
                                <div>
                                    <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                        Model Name<span className="text-red-600 ml-1 text-sm">*</span>
                                    </label>
                                    <input type="text" name="ModelName" value={formData.ModelName} onChange={handleInputChange} className={getInputClass('ModelName')} placeholder="e.g., CX-5" required />
                                </div>
                                <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Purchasing Year</label><input type="number" name="PurchasingYear" value={formData.PurchasingYear} onChange={handleInputChange} className={getInputClass('PurchasingYear')} placeholder="e.g., 2023" /></div>
                            </div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Mileage In</label><input type="number" name="MiledgeIn" value={formData.MiledgeIn} onChange={handleInputChange} className={getInputClass('MiledgeIn')} placeholder="e.g., 15000" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Mileage Out</label><input type="number" name="MiledgeOut" value={formData.MiledgeOut} onChange={handleInputChange} className={getInputClass('MiledgeOut')} placeholder="e.g., 15010" /></div>
                        </div>
                    </FormCard>

                    <FormCard title="Dates & Customer" icon="fa-user-clock">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">R/O Open Date</label><input type="date" name="RO_open_date" value={formData.RO_open_date} onChange={handleInputChange} className={getInputClass('RO_open_date')} /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Repair Date</label><input type="date" name="RepairDate" value={formData.RepairDate} onChange={handleInputChange} className={getInputClass('RepairDate')} /></div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                    Customer Name<span className="text-red-600 ml-1 text-sm">*</span>
                                </label>
                                <input type="text" name="CustomerName" value={formData.CustomerName} onChange={handleInputChange} className={getInputClass('CustomerName')} placeholder="Enter Customer Name" required />
                            </div>
                            <div className="md:col-span-2"><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Customer Complaint / R/O Description</label><textarea name="RO_Description" value={formData.RO_Description} onChange={handleInputChange} className={getInputClass('RO_Description')} rows="3" placeholder="Describe the customer complaint..."></textarea></div>
                        </div>
                    </FormCard>

                    <FormCard title="Technical Details" icon="fa-microchip">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
                            <div className="lg:col-span-2"><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Part Number Mainly Concerned (PNMC)</label><input type="text" name="PNMC" value={formData.PNMC} onChange={handleInputChange} className={getInputClass('PNMC')} placeholder="Enter PNMC" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">PNMC Quantity</label><input type="number" name="PNMC_Quantity" value={formData.PNMC_Quantity} onChange={handleInputChange} className={getInputClass('PNMC_Quantity')} placeholder="e.g., 1" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Warranty Type Code</label><input type="text" name="WarrantyType_Code" value={formData.WarrantyType_Code} onChange={handleInputChange} className={getInputClass('WarrantyType_Code')} placeholder="e.g., W" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Symptom Code</label><input type="text" name="SymptomCode" value={formData.SymptomCode} onChange={handleInputChange} className={getInputClass('SymptomCode')} placeholder="e.g., S01" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Damage Code</label><input type="text" name="DamageCode" value={formData.DamageCode} onChange={handleInputChange} className={getInputClass('DamageCode')} placeholder="e.g., D01" /></div>
                            <div className="md:col-span-3"><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Related Parts (comma-separated codes)</label><input type="text" name="RelatedParts" value={formData.RelatedParts} onChange={handleInputChange} className={getInputClass('RelatedParts')} placeholder="e.g., PartCodeA, PartCodeB" /></div>
                            <div className="md:col-span-3"><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Repair Location(s) (comma-separated)</label><input type="text" name="RepairLocation" value={formData.RepairLocation} onChange={handleInputChange} className={getInputClass('RepairLocation')} placeholder="e.g., Passenger side, Front bumper" /></div>
                        </div>
                    </FormCard>

                    <FormCard title="Parts Used" icon="fa-cogs"
                        headerContent={<button type="button" onClick={() => addRow('PartsUsed')} className="btn btn-secondary py-2 px-4 text-[10px]"><i className="fas fa-plus mr-2"></i> Add Part</button>}>
                        <div className="flex items-center mb-4">
                            <span className="block text-xs font-semibold text-text-muted uppercase tracking-widest">Parts Logged</span>
                            <span className="text-red-600 ml-1 text-sm">*</span>
                        </div>
                        <div className={`overflow-x-auto ${errors.PartsUsed ? 'border-2 border-red-600 p-1' : ''}`}>
                            <table className="min-w-full">
                                <thead className="bg-background border-b border-mazda-border"><tr><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Part Name</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Quantity</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Price ($)</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Action</th></tr></thead>
                                <tbody className="bg-surface divide-y divide-mazda-border">
                                    {formData.PartsUsed.map((part, index) => {
                                        const hasRowError = errors[`part_${index}`];
                                        return (
                                            <tr key={index} className="hover:bg-background transition-colors">
                                                <td className="p-2"><input type="text" name="part_name" value={part.part_name} onChange={(e) => handleDynamicChange(index, e, 'PartsUsed')} className={`form-input w-full ${hasRowError && !part.part_name ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Part Name" required /></td>
                                                <td className="p-2"><input type="number" name="part_quantity" value={part.part_quantity} onChange={(e) => handleDynamicChange(index, e, 'PartsUsed')} className={`form-input w-full ${hasRowError && (part.part_quantity === '' || part.part_quantity === undefined) ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Qty" min="0" required /></td>
                                                <td className="p-2"><input type="number" name="part_price" value={part.part_price} onChange={(e) => handleDynamicChange(index, e, 'PartsUsed')} className={`form-input w-full ${hasRowError && (part.part_price === '' || part.part_price === undefined) ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Price" step="0.01" min="0" required /></td>
                                                <td className="p-2 text-center"><button type="button" onClick={() => removeRow(index, 'PartsUsed')} className="text-text-muted hover:text-red-600 p-2 transition-colors"><i className="fas fa-trash-alt"></i></button></td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            {formData.PartsUsed.length === 0 && (
                                <div className="p-6 text-center text-xs text-text-muted uppercase tracking-widest">
                                    No parts added yet. Click "Add Part" to begin.
                                </div>
                            )}
                        </div>
                    </FormCard>

                    <FormCard title="Labor Operations" icon="fa-hard-hat"
                        headerContent={<button type="button" onClick={() => addRow('LaborOpDetails')} className="btn btn-secondary py-2 px-4 text-[10px]"><i className="fas fa-plus mr-2"></i> Add Labor</button>}>
                        <div className="flex items-center mb-4">
                            <span className="block text-xs font-semibold text-text-muted uppercase tracking-widest">Labor Logged</span>
                            <span className="text-red-600 ml-1 text-sm">*</span>
                        </div>
                        <div className={`overflow-x-auto ${errors.LaborOpDetails ? 'border-2 border-red-600 p-1' : ''}`}>
                            <table className="min-w-full">
                                <thead className="bg-background border-b border-mazda-border"><tr><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Labor Op Code</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Labor Hours</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Technician ID</th><th className="px-4 py-3 text-left text-[10px] font-semibold text-text-muted uppercase tracking-widest">Action</th></tr></thead>
                                <tbody className="bg-surface divide-y divide-mazda-border">
                                    {formData.LaborOpDetails.map((op, index) => {
                                        const hasRowError = errors[`labor_${index}`];
                                        return (
                                            <tr key={index} className="hover:bg-background transition-colors">
                                                <td className="p-2"><input type="text" name="LaborOpCode" value={op.LaborOpCode} onChange={(e) => handleDynamicChange(index, e, 'LaborOpDetails')} className={`form-input w-full ${hasRowError && !op.LaborOpCode ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Op Code" required /></td>
                                                <td className="p-2"><input type="number" name="LaborHours" value={op.LaborHours} onChange={(e) => handleDynamicChange(index, e, 'LaborOpDetails')} className={`form-input w-full ${hasRowError && (op.LaborHours === '' || op.LaborHours === undefined) ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Hours" step="0.1" min="0" required /></td>
                                                <td className="p-2"><input type="text" name="TechnicianID" value={op.TechnicianID} onChange={(e) => handleDynamicChange(index, e, 'LaborOpDetails')} className={`form-input w-full ${hasRowError && !op.TechnicianID ? 'border-red-600 bg-red-50/50' : ''}`} placeholder="Tech ID" required /></td>
                                                <td className="p-2 text-center"><button type="button" onClick={() => removeRow(index, 'LaborOpDetails')} className="text-text-muted hover:text-red-600 p-2 transition-colors"><i className="fas fa-trash-alt"></i></button></td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                            {formData.LaborOpDetails.length === 0 && (
                                <div className="p-6 text-center text-xs text-text-muted uppercase tracking-widest">
                                    No labor operations added yet. Click "Add Labor" to begin.
                                </div>
                            )}
                        </div>
                    </FormCard>

                    <FormCard title="Financials & Dealer Information" icon="fa-file-invoice-dollar">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Sublet Amount ($)</label><input type="number" name="SubletAmount" value={formData.SubletAmount} onChange={handleInputChange} className={getInputClass('SubletAmount')} placeholder="e.g., 50.00" step="0.01" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Sublet Code</label><input type="text" name="SubletCode" value={formData.SubletCode} onChange={handleInputChange} className={getInputClass('SubletCode')} placeholder="Enter Sublet Code" /></div>
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                                    Estimated Total Amount ($)<span className="text-red-600 ml-1 text-sm">*</span>
                                </label>
                                <input type="number" name="EstimatedAmount" value={formData.EstimatedAmount} onChange={handleInputChange} className={getInputClass('EstimatedAmount')} placeholder="e.g., 450.50" required step="0.01" />
                            </div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Service Advisor</label><input type="text" name="ServiceAdvisor" value={formData.ServiceAdvisor} onChange={handleInputChange} className={getInputClass('ServiceAdvisor')} placeholder="Enter Service Advisor Name" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Dealer Code</label><input type="text" name="DealerCode" value={formData.DealerCode} onChange={handleInputChange} className={getInputClass('DealerCode')} placeholder="Enter Dealer Code" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Dealer Name</label><input type="text" name="DealerName" value={formData.DealerName} onChange={handleInputChange} className={getInputClass('DealerName')} placeholder="Enter Dealer Name" /></div>
                            <div><label className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">Dealer Address</label><textarea name="DealerAddress" value={formData.DealerAddress} onChange={handleInputChange} className={getInputClass('DealerAddress')} rows="2" placeholder="Enter Dealer Address"></textarea></div>
                        </div>
                    </FormCard>

                    <div className="flex flex-wrap justify-end mt-12 gap-4">
                        <button type="reset" className="btn btn-secondary"><i className="fas fa-undo mr-3"></i> Reset Form</button>
                        <button type="button" onClick={handlePredict} className="btn btn-secondary"><i className="fas fa-robot mr-3"></i> Predict Status</button>
                        <button type="submit" className="btn btn-primary"><i className="fas fa-paper-plane mr-3"></i> Submit Claim</button>
                    </div>
                </form>
            </main>

            <footer className="text-center py-8 mt-12 border-t border-mazda-border">
                <p className="text-text-muted text-[10px] uppercase tracking-widest font-semibold">© {new Date().getFullYear()} YOUR COMPANY | ALL RIGHTS RESERVED</p>
            </footer>
        </div>
    );
}

export default WarrentySubmitPage;